import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Heart,
  Droplet,
  Package,
  Activity,
  TrendingUp,
  Truck,
  Clock,
} from "lucide-react";

import "../../assets/css/donordashboard/DonorDashboard.css";
import MasterLayout from "../../components/layout/MasterLayout";
import AppContext from "../../context/AppContext"; // ⚠️ path check kar lein, agar alag hai to yahan update kar dein

const DonorDashboard = () => {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);

  // ===== Live Counts State =====
  const [foodAvailable, setFoodAvailable] = useState(0);
  const [foodUrgent, setFoodUrgent] = useState(0);

  const [bloodCritical, setBloodCritical] = useState(0);

  const [medicineItems, setMedicineItems] = useState(0);
  const [medicineNearExpiry, setMedicineNearExpiry] = useState(0);

  const [loadingCards, setLoadingCards] = useState(true);

  const stats = [
    { icon: Activity, label: "Total Donations", value: "1,234", color: "#14b8a6" },
    { icon: Truck, label: "Delivered Today", value: "89", color: "#10b981" },
    { icon: Clock, label: "Pending Requests", value: "23", color: "#f59e0b" },
  ];

  const recentActivity = [
    { type: "Food", item: "Rice (50kg)", location: "Madel Town", status: "Pending", statusColor: "#f59e0b" },
    { type: "Blood", item: "O+ (2 Units)", location: "Master City", status: "Approved", statusColor: "#10b981" },
    { type: "Medicine", item: "Insulin", location: "Garden Town", status: "Delivered", statusColor: "#14b8a6" },
    { type: "Food", item: "Meals (100)", location: "Green Valley", status: "Delivered", statusColor: "#14b8a6" },
    { type: "Blood", item: "AB+ (1 Unit)", location: "Master City", status: "Urgent", statusColor: "#ef4444" },
  ];

  const bloodGroups = ["A+", "O-", "AB+", "B+", "O+"];

  // ===== Fetch Live Counts =====
  useEffect(() => {
    const fetchCardData = async () => {
      try {
        const [foodRes, bloodRes, medRes] = await Promise.all([
          axios.get(`${baseUrl}/donor/dashboard/viewAllDonation`),
          axios.get(`${baseUrl}/donor/dashboard/viewAllBloodDonations`),
          axios.get(`${baseUrl}/donor/dashboard/viewAllMedicine`),
        ]);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // ---------- FOOD ----------
        const foodList = foodRes.data?.donation || [];
        setFoodAvailable(foodList.length);

        const urgentFoodCount = foodList.filter((item) => {
          if (!item.pickup_date) return false;
          const pickupDate = new Date(item.pickup_date);
          const diffDays = (pickupDate - today) / (1000 * 60 * 60 * 24);
          return diffDays >= 0 && diffDays <= 1; // aaj ya kal pickup
        }).length;
        setFoodUrgent(urgentFoodCount);

        // ---------- BLOOD ----------
        const bloodList = bloodRes.data?.donation || [];
        const criticalBloodCount = bloodList.filter(
          (item) => item.status === "pending"
        ).length;
        setBloodCritical(criticalBloodCount);

        // ---------- MEDICINE ----------
        const medList = medRes.data?.donations || [];
        const availableMed = medList.filter(
          (item) => item.donation !== "collected"
        );
        setMedicineItems(availableMed.length);

        const nearExpiryCount = availableMed.filter((item) => {
          if (!item.expiry_date) return false;
          const expiryDate = new Date(item.expiry_date);
          const diffDays = (expiryDate - today) / (1000 * 60 * 60 * 24);
          return diffDays >= 0 && diffDays <= 30; // 30 din ke andar expire
        }).length;
        setMedicineNearExpiry(nearExpiryCount);
      } catch (error) {
        console.error("Error fetching dashboard card data:", error);
      } finally {
        setLoadingCards(false);
      }
    };

    if (baseUrl) {
      fetchCardData();
    }
  }, [baseUrl]);

  return (
    <MasterLayout>
      <div className="donor-main-content">

        {/* Welcome Section */}
        <div className="donor-welcome-section">
          <h1 className="donor-welcome-text">Welcome back, Ahmed! 👋</h1>
          <p className="donor-sub-text">Make a difference today</p>
        </div>

        {/* Main Cards */}
        <div className="donor-cards-grid">

          {/* Food Card */}
          <div className="donor-card donor-card-food">
            <div className="donor-card-header">
              <div className="donor-card-icon donor-card-icon-food">
                <Package size={28} color="#10b981" />
              </div>
              <span className="donor-urgent-badge donor-urgent-badge-warning">
                {loadingCards ? "..." : `${foodUrgent} Urgent`}
              </span>
            </div>
            <h3 className="donor-card-title">Food Donations</h3>
            <p className="donor-card-count">
              {loadingCards ? "..." : `${foodAvailable} Available`}
            </p>
            <p className="donor-card-subtext">Fresh food items ready for distribution</p>
            <button
              className="donor-card-button donor-card-button-food"
              onClick={() => navigate("/donor/donate-food")}
            >
              Donate Food
            </button>
          </div>

          {/* Blood Card */}
          <div className="donor-card donor-card-blood">
            <div className="donor-card-header">
              <div className="donor-card-icon donor-card-icon-blood">
                <Droplet size={28} color="#ef4444" />
              </div>
              <span className="donor-urgent-badge donor-urgent-badge-danger">🚨 Urgent</span>
            </div>
            <h3 className="donor-card-title">Blood Requests</h3>
            <p className="donor-card-count">
              {loadingCards ? "..." : `${bloodCritical} Critical`}
            </p>
            <div className="donor-blood-groups">
              {bloodGroups.map((group) => (
                <span key={group} className="donor-blood-group">{group}</span>
              ))}
            </div>
            <button
              className="donor-card-button donor-card-button-blood"
              onClick={() => navigate("/donor/donate-blood")}
            >
              Donate Blood
            </button>
          </div>

          {/* Medicine Card */}
          <div className="donor-card donor-card-medicine">
            <div className="donor-card-header">
              <div className="donor-card-icon donor-card-icon-medicine">
                <Heart size={28} color="#14b8a6" />
              </div>
              <span className="donor-urgent-badge donor-urgent-badge-warning">
                {loadingCards ? "..." : `${medicineNearExpiry} Near Expiry`}
              </span>
            </div>
            <h3 className="donor-card-title">Medicines</h3>
            <p className="donor-card-count">
              {loadingCards ? "..." : `${medicineItems} Items`}
            </p>
            <p className="donor-card-subtext">Essential medicines & supplements</p>
            <button
              className="donor-card-button donor-card-button-medicine"
              onClick={() => navigate("/donor/donate-medicine")}
            >
              Donate Medicine
            </button>
          </div>

        </div>

        {/* Stats Row */}
        <div className="donor-stats-grid">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="donor-stat-card">
                <div className="donor-stat-icon" style={{ backgroundColor: `${stat.color}20` }}>
                  <Icon size={24} color={stat.color} />
                </div>
                <div>
                  <p className="donor-stat-value">{stat.value}</p>
                  <p className="donor-stat-label">{stat.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* AI Suggestion Banner */}
        <div className="donor-suggestion-banner">
          <div className="donor-suggestion-icon">🔍</div>
          <div>
            <p className="donor-suggestion-title">Suggested Donation</p>
            <p className="donor-suggestion-text">
              Blood O+ urgently needed in Lahore - 3 hospitals requesting
            </p>
          </div>
          <button className="donor-suggestion-button" onClick={() => navigate("/donor/donate-blood")}>
            View Details
          </button>
        </div>

        {/* Recent Activity Table */}
        <div className="donor-table-container">
          <h3 className="donor-table-title">Recent Activity</h3>
          <div className="donor-table">
            <div className="donor-table-header">
              <div className="donor-table-cell">Type</div>
              <div className="donor-table-cell">Item</div>
              <div className="donor-table-cell">Location</div>
              <div className="donor-table-cell">Status</div>
            </div>
            {recentActivity.map((activity, index) => (
              <div key={index} className="donor-table-row">
                <div className="donor-table-cell">
                  <span className={`donor-type-tag donor-type-tag-${activity.type.toLowerCase()}`}>
                    {activity.type}
                  </span>
                </div>
                <div className="donor-table-cell">{activity.item}</div>
                <div className="donor-table-cell">{activity.location}</div>
                <div className="donor-table-cell">
                  <span
                    className="donor-status-badge"
                    style={{
                      backgroundColor: `${activity.statusColor}20`,
                      color: activity.statusColor,
                    }}
                  >
                    {activity.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Floating Button */}
        <button className="donor-emergency-button" onClick={() => navigate("/donor/donate-blood")}>
          🚨 Emergency Blood Request
        </button>

      </div>
    </MasterLayout>
  );
};

export default DonorDashboard;